import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFavicon } from '../utils/useFavicon';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #FAF8F5;
    color: #2C2C2C;
    -webkit-font-smoothing: antialiased;
  }
  .admin-shell {
    display: flex;
    min-height: 100vh;
  }
  .admin-sidebar {
    width: 240px;
    background: #fff;
    border-right: 1px solid #EDE9E4;
    display: flex;
    flex-direction: column;
    padding: 0;
    position: fixed;
    top: 0; left: 0; bottom: 0;
    z-index: 100;
    box-shadow: 2px 0 12px rgba(0,0,0,0.04);
    transition: transform 0.25s ease;
  }
  .admin-sidebar.collapsed {
    transform: translateX(-240px);
  }
  .sidebar-logo {
    padding: 24px 20px 20px;
    font-size: 0.95rem;
    font-weight: 700;
    color: #2C2C2C;
    letter-spacing: -0.01em;
    border-bottom: 1px solid #EDE9E4;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .sidebar-logo img { height: 26px; width: auto; display: block; }
  .sidebar-logo span {
    color: #E8845A;
  }
  .sidebar-nav {
    flex: 1;
    padding: 12px 0;
    overflow-y: auto;
  }
  .sidebar-nav a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 20px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #555;
    text-decoration: none;
    border-radius: 0;
    transition: background 0.15s, color 0.15s;
    position: relative;
  }
  .sidebar-nav a:hover {
    background: #FFF5F0;
    color: #E8845A;
  }
  .sidebar-nav a.active {
    background: #FFF5F0;
    color: #E8845A;
    font-weight: 600;
  }
  .sidebar-nav a.active::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: #E8845A;
    border-radius: 0 2px 2px 0;
  }
  .sidebar-footer {
    padding: 16px 20px;
    border-top: 1px solid #EDE9E4;
    font-size: 0.8rem;
    color: #888;
  }
  .sidebar-footer .footer-email {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 10px;
    font-weight: 500;
    color: #444;
  }
  .sidebar-footer button {
    background: none;
    border: 1px solid #E0DDD8;
    color: #666;
    font-family: inherit;
    font-size: 0.8rem;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .sidebar-footer button:hover {
    background: #FFF5F0;
    color: #E8845A;
    border-color: #E8845A;
  }
  .admin-main {
    flex: 1;
    margin-left: 240px;
    min-height: 100vh;
    background: #FAF8F5;
    transition: margin-left 0.25s ease;
  }
  .admin-main.full-width { margin-left: 0; }
  .admin-topbar {
    display: flex;
    align-items: center;
    padding: 0 28px;
    height: 56px;
    border-bottom: 1px solid #EDE9E4;
    background: #fff;
    gap: 14px;
    position: sticky;
    top: 0;
    z-index: 50;
  }
  .topbar-toggle {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    color: #666;
    border-radius: 6px;
    display: none;
    transition: background 0.15s;
  }
  .topbar-toggle:hover { background: #F0EDE8; }
  .topbar-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2C2C2C;
  }
  .admin-content {
    padding: 28px;
    max-width: 1200px;
  }
  @media (max-width: 768px) {
    .admin-sidebar { transform: translateX(-240px); }
    .admin-sidebar.open { transform: translateX(0); }
    .admin-main { margin-left: 0; }
    .topbar-toggle { display: flex; }
    .admin-content { padding: 16px; }
  }
  .sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.3);
    z-index: 99;
  }
  .sidebar-overlay.visible { display: block; }
`;

const NAV = [
  { path: '/admin/dashboard',        icon: '🏠', label: 'Dashboard' },
  { path: '/admin/users',            icon: '👥', label: 'Users' },
  { path: '/admin/usage',            icon: '📈', label: 'Usage' },
  { path: '/admin/unit-economics',   icon: '💰', label: 'Unit economics' },
  { path: '/admin/conversations',    icon: '💬', label: 'Conversations' },
  { path: '/admin/intake-questions', icon: '📋', label: 'Intake Questions' },
  { path: '/admin/voices',           icon: '🗣️', label: 'Voices' },
  { path: '/admin/llm-config',       icon: '🧠', label: 'LLM' },
  { path: '/admin/analytics',         icon: '📊', label: 'Analytics' },
  { path: '/admin/cards/effectiveness', icon: '🃏', label: 'Cards effectiveness' },
  // #766 Story E — siblings to the existing "Cards" link from #765.
  { path: '/admin/cards/variety',     icon: '🌳', label: 'Cards variety' },
  { path: '/admin/cards/mood-matrix', icon: '🎭', label: 'Cards × mood' },
  // #806 — wildcard taxonomy-gap detector
  { path: '/admin/cards/wildcard-gaps', icon: '🌱', label: 'Wildcard gaps' },
  { path: '/admin/conversations/disengaged', icon: '📉', label: 'Disengaged calls' },
  // #959 — Crons observability (admin can see what scheduled jobs
  // ran when, and drill into the News Crawler detail view).
  { path: '/admin/crons',            icon: '⏱️', label: 'Crons' },
  { path: '/admin/security',         icon: '🔒', label: 'Security' },
  { path: '/admin/system',           icon: '⚙️', label: 'System' },
];

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Admin surface — navy favicon. AdminLayout wraps every admin page
  // (AdminDashboard, AdminUsers, AdminVoices, etc.), so the hook here
  // covers the whole /admin tree without per-page wiring. Note that
  // AdminLogin doesn't use AdminLayout — it gets its own useFavicon
  // call below.
  useFavicon('admin');

  const email = (() => {
    try {
      const token = localStorage.getItem('am_admin_token');
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.email || '';
    } catch { return ''; }
  })();

  function logout() {
    localStorage.removeItem('am_admin_token');
    navigate('/admin/login');
  }

  return (
    <>
      <style>{styles}</style>
      <div className="admin-shell">
        {/* Sidebar overlay for mobile */}
        <div
          className={`sidebar-overlay${open ? ' visible' : ''}`}
          onClick={() => setOpen(false)}
        />

        {/* Sidebar */}
        <nav className={`admin-sidebar${open ? ' open' : ''}`}>
          <div className="sidebar-logo">
            {/* #238 PR-2 — icon mark + wordmark + Admin tag */}
            <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" />
            Amigo Mio&nbsp;<span>Admin</span>
          </div>
          <div className="sidebar-nav">
            {NAV.map(item => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={isActive ? 'active' : ''}
                  onClick={() => setOpen(false)}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </Link>
              );
            })}
          </div>
          <div className="sidebar-footer">
            {email && <div className="footer-email">{email}</div>}
            <button onClick={logout}>Log out</button>
          </div>
        </nav>

        {/* Main area */}
        <main className="admin-main">
          <div className="admin-topbar">
            <button
              className="topbar-toggle"
              onClick={() => setOpen(v => !v)}
              aria-label="Toggle sidebar"
            >
              ☰
            </button>
            <span className="topbar-title">{title}</span>
          </div>
          <div className="admin-content">
            {children}
          </div>
        </main>
      </div>
    </>
  );
}
